/**
 * Backend API client. Production calls use the same Cognito ID token as the
 * dashboard, plus a stable device id for audit/device management. Local/dev can
 * still fall back to the legacy X-Field-Device-Token path when auth is absent.
 */

import {
  FieldReportAckSchema,
  FieldReportSchema,
  FieldJobBootstrapSchema,
  FieldTodaysJobSchema,
  type FieldReport,
  type FieldReportAck,
  type FieldJobBootstrap,
  type FieldTodaysJob,
} from '@cer-autofill/shared';
import { z } from 'zod';
import { backendBaseUrl, log } from './env.js';
import { getSession, updateCognitoTokens } from './db/index.js';
import { refreshCognitoTokens } from './auth.js';
import { reportReauthRequired } from './lib/auth-diag.js';

/** Shown when a silent refresh cannot recover an expired Cognito session. */
export const SIGN_IN_AGAIN = 'Please sign in again';

class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Refresh the ID token when it is within this window of expiring.
 *
 * Generous on purpose. The old 30s margin meant a tablet that had been asleep
 * woke up with a token that was already dead, and the failure surfaced as
 * whatever the inspector happened to tap first. A minute either way costs
 * nothing — the refresh token is good for 30 days.
 */
const REFRESH_MARGIN_MS = 5 * 60_000;
/** Abort hung cellular requests so sync cannot sit "running" forever. */
const FETCH_TIMEOUT_MS = 60_000;

async function authedFetch(path: string, init: RequestInit = {}): Promise<Response> {
  try {
    const res = await sendAuthed(path, init);
    if (!(await responseSaysNotAuthorized(res))) return finish(res, path);
  } catch (error) {
    // A failed refresh already is the sign-in prompt. Retrying it would just
    // show the raw Cognito exception on the second failure.
    if (!isNotAuthorized(error)) throw error;
  }
  return retryAfterRefresh(path, init);
}

/**
 * Cognito answers a dead refresh with `NotAuthorizedException`. That string
 * must not reach the inspector. Refresh once and retry the original request
 * once; if the refresh fails, ask them to sign in again.
 */
async function retryAfterRefresh(path: string, init: RequestInit): Promise<Response> {
  const session = await getSession();
  if (!session?.refreshToken) {
    reportReauthRequired('refresh_failed');
    throw new ApiError(401, SIGN_IN_AGAIN);
  }
  try {
    await refreshSession(session.refreshToken);
  } catch {
    reportReauthRequired('refresh_failed');
    throw new ApiError(401, SIGN_IN_AGAIN);
  }
  try {
    const res = await sendAuthed(path, init);
    if (await responseSaysNotAuthorized(res)) {
      reportReauthRequired('refresh_failed');
      throw new ApiError(401, SIGN_IN_AGAIN);
    }
    return finish(res, path);
  } catch (error) {
    if (isNotAuthorized(error)) {
      reportReauthRequired('refresh_failed');
      throw new ApiError(401, SIGN_IN_AGAIN);
    }
    throw error;
  }
}

function isNotAuthorized(error: unknown): boolean {
  if (error instanceof ApiError) {
    if (error.message === SIGN_IN_AGAIN) return false;
    return (
      error.status === 401 ||
      error.message.includes('NotAuthorized') ||
      (error.body?.includes('NotAuthorized') ?? false)
    );
  }
  return error instanceof Error && error.message.includes('NotAuthorized');
}

async function responseSaysNotAuthorized(res: Response): Promise<boolean> {
  if (res.ok) return false;
  if (res.status === 401) return true;
  const text = await res
    .clone()
    .text()
    .catch(() => '');
  return text.includes('NotAuthorized');
}

export function userFacingApiError(error: unknown): string {
  if (error instanceof ApiError && error.message === SIGN_IN_AGAIN) return SIGN_IN_AGAIN;
  const message =
    error instanceof ApiError
      ? `${error.status}: ${error.message}`
      : error instanceof Error
        ? error.message
        : 'Request failed';
  return message.includes('NotAuthorized') ? SIGN_IN_AGAIN : message;
}

async function sendAuthed(path: string, init: RequestInit): Promise<Response> {
  const headers = new Headers(init.headers);
  const session = await sessionWithFreshToken();
  if (session?.deviceId) headers.set('X-Field-Device-Id', session.deviceId);
  if (session?.idToken) {
    headers.set('Authorization', `Bearer ${session.idToken}`);
  } else if (session?.deviceToken) {
    headers.set('X-Field-Device-Token', session.deviceToken);
  }
  if (!headers.has('Content-Type') && init.body && typeof init.body === 'string') {
    headers.set('Content-Type', 'application/json');
  }
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  const onAbort = () => controller.abort();
  init.signal?.addEventListener('abort', onAbort);
  try {
    return await fetch(`${backendBaseUrl}${path}`, {
      ...init,
      headers,
      signal: controller.signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new ApiError(408, `Request timed out after ${FETCH_TIMEOUT_MS}ms on ${path}`);
    }
    throw error;
  } finally {
    clearTimeout(timer);
    init.signal?.removeEventListener('abort', onAbort);
  }
}

async function finish(res: Response, path: string): Promise<Response> {
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    let hint = '';
    if (path.endsWith('/photo') || path.endsWith('/photo/upload-url')) {
      try {
        const error = (JSON.parse(body) as { error?: unknown }).error;
        const hints: Record<string, string> = {
          'missing-fields':
            'Photo upload was incomplete. Retry from Photos; the local photo is retained.',
          'invalid-photo-metadata':
            'Photo details could not be accepted. Retry from Photos; the local photo is retained.',
          'multipart-parse-failed':
            'Photo upload could not be read. Retry when connected; the local photo is retained.',
          'unsupported-media-type':
            'Choose a JPEG, PNG or WebP photo (or a PDF for signed consent).',
        };
        if (typeof error === 'string') hint = hints[error] ?? '';
      } catch {
        /* Keep generic HTTP errors when there is no structured response. */
      }
    }
    throw new ApiError(res.status, hint || `${res.status} ${res.statusText} on ${path}`, body);
  }
  return res;
}

function jwtExpiryMs(idToken: string): number | undefined {
  try {
    const [, payload] = idToken.split('.');
    if (!payload) return undefined;
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const decoded = JSON.parse(atob(padded)) as { exp?: unknown };
    return typeof decoded.exp === 'number' ? decoded.exp * 1000 : undefined;
  } catch {
    return undefined;
  }
}

/** True when either stored expiry or the ID token `exp` claim is inside the refresh window. */
function tokenNeedsRefresh(session: {
  idToken?: string | undefined;
  expiresAt?: number | undefined;
}): boolean {
  if (!session.idToken) return false;
  const clocks = [session.expiresAt, jwtExpiryMs(session.idToken)].filter(
    (value): value is number => typeof value === 'number',
  );
  if (clocks.length === 0) return true;
  return clocks.some((exp) => Date.now() >= exp - REFRESH_MARGIN_MS);
}

async function sessionWithFreshToken() {
  const session = await getSession();
  if (!session?.idToken) return session;
  if (!tokenNeedsRefresh(session)) return session;
  if (!session.refreshToken) {
    reportReauthRequired('refresh_failed');
    throw new ApiError(401, SIGN_IN_AGAIN);
  }
  try {
    await refreshSession(session.refreshToken);
  } catch (error) {
    if (isNotAuthorized(error)) {
      reportReauthRequired('refresh_failed');
      throw new ApiError(401, SIGN_IN_AGAIN);
    }
    throw error;
  }
  const refreshed = await getSession();
  if (!refreshed?.idToken) {
    reportReauthRequired('refresh_failed');
    throw new ApiError(401, SIGN_IN_AGAIN);
  }
  return refreshed;
}

/**
 * Keep the session alive across a whole working day without the inspector
 * noticing it.
 *
 * The ID token lasts an hour; the refresh token lasts 30 days. Until now the
 * only thing that ever spent the refresh token was an outbound API call, so a
 * tablet left in the van over lunch came back with a dead token and failed on
 * whatever the inspector touched first — which reads as "it logged me out".
 *
 * Refreshing when the app returns to the foreground fixes the common case
 * (that's when the tablet has been idle); the interval covers a screen left
 * awake on one job for hours. Both are best-effort: failures are swallowed
 * because `authedFetch` still refreshes and retries on its own, and an
 * inspector working offline must not be signed out for it.
 */
export function keepSessionFresh(): () => void {
  const tick = () => {
    if (!navigator.onLine) return;
    void getSession()
      .then((session) => {
        if (!session?.refreshToken || !session.idToken) return;
        if (!tokenNeedsRefresh(session)) return;
        return refreshSession(session.refreshToken);
      })
      .catch((error: unknown) => log('background token refresh failed', error));
  };

  const onVisible = () => {
    if (document.visibilityState === 'visible') tick();
  };
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('focus', tick);
  window.addEventListener('online', tick);
  const timer = window.setInterval(tick, 10 * 60_000);
  tick();

  return () => {
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('focus', tick);
    window.removeEventListener('online', tick);
    window.clearInterval(timer);
  };
}

let refreshInFlight: Promise<void> | null = null;

function refreshSession(refreshToken: string): Promise<void> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = refreshCognitoTokens({ refreshToken })
    .then((result) =>
      updateCognitoTokens({
        idToken: result.IdToken!,
        ...(result.AccessToken !== undefined ? { accessToken: result.AccessToken } : {}),
        ...(result.ExpiresIn !== undefined ? { expiresInSeconds: result.ExpiresIn } : {}),
        ...(result.RefreshToken !== undefined ? { refreshToken: result.RefreshToken } : {}),
      }),
    )
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
}

/* ────────────────────────────────────────────────────────────────────────────
 * Endpoints
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * The roster. Defaults to today; pass a YYYY-MM-DD `date` to page to another
 * day (look ahead or look back). Today's roster also carries forward anything
 * still unfinished from the past fortnight.
 */
export async function fetchTodaysJobs(date?: string): Promise<FieldTodaysJob[]> {
  const path = date
    ? `/api/field/jobs/today?date=${encodeURIComponent(date)}`
    : '/api/field/jobs/today';
  const res = await authedFetch(path);
  const json: unknown = await res.json();
  return z.array(FieldTodaysJobSchema).parse(json);
}

/** Exact accreditation-code lookup for inspectors who also hold the admin role. */
export async function searchFieldJobs(query: string): Promise<FieldTodaysJob[]> {
  const res = await authedFetch(`/api/field/jobs/search?q=${encodeURIComponent(query.trim())}`);
  const json: unknown = await res.json();
  return z.array(FieldTodaysJobSchema).parse(json);
}

export async function fetchJobBootstrap(jobId: string): Promise<FieldJobBootstrap> {
  const res = await authedFetch(`/api/field/job/${encodeURIComponent(jobId)}`);
  const json: unknown = await res.json();
  return FieldJobBootstrapSchema.parse(json);
}

export async function openConsentPreview(jobId: string): Promise<void> {
  const target = window.open('', '_blank');
  try {
    const response = await authedFetch(
      `/api/field/job/${encodeURIComponent(jobId)}/consent-preview.pdf`,
    );
    const url = URL.createObjectURL(await response.blob());
    if (target) target.location.href = url;
    else window.location.href = url;
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (error) {
    target?.close();
    throw error;
  }
}

export async function submitReport(submission: FieldReport): Promise<FieldReportAck> {
  // Validate locally before sending so we get a fast error vs. a 400 round-trip.
  const parsed = FieldReportSchema.parse(submission);
  log('submitting', parsed.jobId);
  const res = await authedFetch(`/api/field/job/${encodeURIComponent(parsed.jobId)}/submit`, {
    method: 'POST',
    body: JSON.stringify(parsed),
  });
  const ackJson: unknown = await res.json();
  return FieldReportAckSchema.parse(ackJson);
}

export async function uploadPhoto(input: {
  jobId: string;
  photoId: string;
  promptKey: string;
  filename: string;
  /**
   * When the shutter fired — from EXIF where available, otherwise the device
   * clock at capture. MUST be sent: the server falls back to the time the
   * upload lands, and the stored manifest takes precedence over the
   * submission's own photo record in the `photo-timestamps` QA rule. Omitting
   * it means every photo synced after the fact — an evening sync, a day spent
   * offline — is timestamped outside its own inspection window.
   */
  capturedAt: string;
  /** Shared across one bulk import, so the audit trail can group them. */
  importBatchId?: string;
  /** Evidence-strip height recorded at capture, stored on the manifest. */
  evidenceFooterPx?: number;
  blob: Blob;
}): Promise<{ storageKey: string; uploadedAt: string } | undefined> {
  const mimeType = input.blob.type || 'image/jpeg';
  // Prefer direct-to-S3 via a short-lived PUT URL. Fall back to multipart when
  // the store has no presigner (local-fs → 501) or the mint request fails.
  try {
    const res = await authedFetch(
      `/api/field/job/${encodeURIComponent(input.jobId)}/photo/upload-url`,
      {
        method: 'POST',
        body: JSON.stringify({
          photoId: input.photoId,
          promptKey: input.promptKey,
          filename: input.filename,
          mimeType,
          byteSize: input.blob.size,
          capturedAt: input.capturedAt,
          ...(input.importBatchId !== undefined ? { importBatchId: input.importBatchId } : {}),
          ...(input.evidenceFooterPx !== undefined
            ? { evidenceFooterPx: input.evidenceFooterPx }
            : {}),
        }),
      },
    );
    const body: unknown = await res.json();
    const parsed = z.object({ uploadUrl: z.string().url() }).parse(body);
    const placed = serverPhotoVersion(body);
    // Same hung-cellular guard as authedFetch — a stuck PUT must not pin the
    // sync worker forever.
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    let putRes: Response;
    try {
      putRes = await fetch(parsed.uploadUrl, {
        method: 'PUT',
        body: input.blob,
        headers: { 'Content-Type': mimeType },
        signal: controller.signal,
      });
    } finally {
      clearTimeout(timer);
    }
    if (!putRes.ok) {
      throw new ApiError(putRes.status, `S3 PUT failed: ${putRes.status} ${putRes.statusText}`);
    }
    return placed;
  } catch (error) {
    if (error instanceof ApiError && error.status !== 501) throw error;
    // Reached on mint 501 and on fetch-level PUT failures (CSP, CORS, network
    // drop) — those surface as TypeError, not ApiError. The manifest is
    // already minted at this point, so if the multipart below also fails the
    // server is left holding a manifest with no bytes (dangling until the
    // photo eventually uploads under the same id).
    log('presigned photo upload unavailable; falling back to multipart', error);
  }

  const form = new FormData();
  form.set('photoId', input.photoId);
  form.set('promptKey', input.promptKey);
  form.set('filename', input.filename);
  form.set('capturedAt', input.capturedAt);
  if (input.importBatchId) form.set('importBatchId', input.importBatchId);
  if (input.evidenceFooterPx !== undefined) {
    form.set('evidenceFooterPx', String(input.evidenceFooterPx));
  }
  form.set('file', input.blob, input.filename);
  const uploaded = await authedFetch(`/api/field/job/${encodeURIComponent(input.jobId)}/photo`, {
    method: 'POST',
    body: form,
  });
  return serverPhotoVersion(await uploaded.json());
}

const ServerPhotoVersionSchema = z.object({
  storageKey: z.string().min(1),
  uploadedAt: z.string().min(1),
});

/** The manifest's storage key and upload time, when the response carries one. */
function serverPhotoVersion(body: unknown): { storageKey: string; uploadedAt: string } | undefined {
  const parsed = z.object({ manifest: ServerPhotoVersionSchema.optional() }).safeParse(body);
  return parsed.success ? parsed.data.manifest : undefined;
}

/**
 * Re-file an already-uploaded photo server-side. The blob does not move — the
 * server keys it by photo id — so this only updates the manifest.
 */
/**
 * Ask the server to turn an uploaded photo. The stored file changes; a 409
 * means either the job is approved or the photo moved since this request was
 * queued. Callers refetch and let the inspector try again.
 */
export async function rotateUploadedPhoto(input: {
  jobId: string;
  photoId: string;
  degrees: 90 | 180 | 270;
  /** Server record this turn was counted from. A replay that still names it is rejected. */
  base?: { storageKey: string; uploadedAt: string };
}): Promise<{ storageKey: string; uploadedAt: string } | undefined> {
  const res = await authedFetch(
    `/api/field/job/${encodeURIComponent(input.jobId)}/photo/${encodeURIComponent(input.photoId)}/rotate`,
    {
      method: 'POST',
      body: JSON.stringify({
        degrees: input.degrees,
        ...(input.base
          ? { baseStorageKey: input.base.storageKey, baseUploadedAt: input.base.uploadedAt }
          : {}),
      }),
    },
  );
  return serverPhotoVersion(await res.json());
}

export async function rekeyPhoto(input: {
  jobId: string;
  photoId: string;
  promptKey: string;
}): Promise<void> {
  await authedFetch(
    `/api/field/job/${encodeURIComponent(input.jobId)}/photo/${encodeURIComponent(input.photoId)}/rekey`,
    {
      method: 'POST',
      body: JSON.stringify({ promptKey: input.promptKey }),
    },
  );
}

/** Remove a photo server-side. The endpoint treats an absent photo as success. */
export async function deletePhotoOnServer(input: {
  jobId: string;
  photoId: string;
}): Promise<void> {
  try {
    await authedFetch(
      `/api/field/job/${encodeURIComponent(input.jobId)}/photo/${encodeURIComponent(input.photoId)}`,
      { method: 'DELETE' },
    );
  } catch (error) {
    if (error instanceof ApiError && error.status === 409) {
      throw new Error(
        'This report is locked for upload. Ask the dashboard reviewer to reopen it before removing evidence.',
      );
    }
    if (error instanceof ApiError && error.status === 403) {
      throw new Error('You no longer have access to change this report.');
    }
    throw new Error(
      'Photo could not be removed. Check your connection and try again; the photo has been kept.',
    );
  }
}

export async function fetchClauseLibrary(): Promise<unknown> {
  const res = await authedFetch('/api/field/clause-library');
  return res.json();
}

export { ApiError };

export async function fetchPhotoPreview(jobId: string, photoId: string): Promise<Blob> {
  const res = await authedFetch(
    `/api/field/job/${encodeURIComponent(jobId)}/photo/${encodeURIComponent(photoId)}`,
  );
  return res.blob();
}
